<template>
  <ModalDialog
    v-model="visible"
    :title="modalTitles.title"
    :is-wide="true"
  >
        <template #header>
          <div>
            <h3 class="modal-title fw-bold">{{ modalTitles.title }}</h3>
            <h5 class="fw-bold">{{ modalTitles.subTitle }}</h5>
          </div>
        </template>
        <template #default>
          <div class="file-upload-data-container">
            <div class="row-data-container px-5">
              <div class="status-icon-container">
                  <button
                    class="status-icon"
                    :aria-describedby="iconAriaDescription"
                  >
                    <span
                      id="iconAriaDescription"
                      class="visually-hidden"
                    >{{ iconAriaDescription }}</span>
                    <i
                      v-if="isUploading && !uploadManager.anyUploadsFailed"
                      class="fa-regular fa-long-arrow-up text-primary"
                      aria-hidden="true"
                    />
                    <i
                      v-else-if="!isUploading && !uploadManager.anyUploadsFailed"
                      class="fa-regular fa-check text-success"
                      aria-hidden="true"
                    />
                    <i
                      v-else
                      class="fa-regular fa-long-arrow-up text-not-uploaded"
                      aria-hidden="true"
                    />
                  </button>
              </div>
              <div class="file-info-container">
                <span
                  v-if="isUploading"
                  class="hidden-xs"
                >{{ $localize('UploadingItems', { itemCount: uploadManager.uploadsCount, percent: uploadManager.currentOverallProgress }) }}</span>
              
                <span
                  v-else
                  class="hidden-xs"
                >{{ $localize('UploadedItems', { itemCount: uploadManager.completedUploadsCounter }) }}</span>
              
                <div
                  class="progress"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-valuenow="uploadManager.currentOverallProgress"
                  :aria-label="progressLabel"
                >
                  <div
                    class="progress-bar"
                    :style="{ width: uploadProgress }"
                    :class="{
                      'bg-success': uploadManager.allUploadsCompleted && !uploadManager.anyUploadsFailed,
                      'bg-primary': isUploading,
                      'bg-danger': uploadManager.allUploadsCompleted && uploadManager.anyUploadsFailed
                    }"
                  />
                </div>
                <span
                  v-if="uploadManager.anyUploadsFailed"
                  class="hidden-xs"
                >{{ $localize('FailedToUploadItems', { itemCount: uploadManager.failedUploadsCounter }) }}</span>
              </div>
            </div>
          </div>
          <hr>
          <div>
            <p v-if="isUploading && !uploadManager.anyUploadsFailed">
              {{ $localize('SessionOverlayTextUploading') }}
            </p>
            <p v-else-if="!isUploading && !uploadManager.anyUploadsFailed">
              {{ $localize('SessionOverlayTextUploaded') }}
            </p>
            <p v-else>
              {{ $localize('SessionOverlayTextUploadFailed') }}
            </p>
            <p v-if="isUploading">
              <a
                ref="newTabLink"
                href="/"
                :aria-describedby="linkAriaDescription"
                @click.prevent="continueInNewTabClick"
              >
                <span>
                  <strong>{{ $localize('SessionOverlayLinkNewTab') }}</strong>
                  <i
                    class="fa-regular fa-external-link ms-3"
                    aria-hidden="true"
                  />
                </span>
              </a>
            </p>
            <p v-else>
              <span
                id="linkAriaDescription"
                class="visually-hidden"
              >{{ linkAriaDescription }}</span>
              <a
                ref="continueHereLink"
                href="/"
                :aria-describedby="linkAriaDescription"
                @click.prevent="continueHereClick"
              >
                <span>
                  <strong>{{ $localize('SessionOverlayLinkHere') }}</strong>
                </span>
              </a>
            </p>
          </div>
        </template>
  </ModalDialog>
</template>

<script lang="ts" setup>
  import { computed, ref, nextTick, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import redirectHelper from '@/Utils/redirectHelper';
  import resourceHelper from '@/Utils/resourceHelper';
  import { useUploadManagerStore } from '@/VueCore/stores/uploadManagerStore';
  import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';

  const router = useRouter();
  const continueHereLink = ref<HTMLElement | null>(null);
  const newTabLink = ref<HTMLElement | null>(null);
  const uploadManager = useUploadManagerStore();
  const visible = defineModel<boolean>({ default: false });

  watch(() => uploadManager.allUploadsCompleted, newValue => {
    if (newValue) {
      nextTick(() => {
        continueHereLink.value?.focus();
      });
    }
  });

  const modalTitles = computed(() => {
    const result = {
      title: '',
      subTitle: ''
    };

    if (isUploading.value && !uploadManager.anyUploadsFailed) {
      result.title = resourceHelper.getString('SessionOverlayTitleUploading');
      result.subTitle = resourceHelper.getString('SessionOverlaySubTitleUploading');
    } else if (!isUploading.value && !uploadManager.anyUploadsFailed) {
      result.title = resourceHelper.getString('SessionOverlayTitleUploaded');
      result.subTitle = resourceHelper.getString('SessionOverlaySubTitleUploadedOrFailed');
    } else {
      result.title = resourceHelper.getString('SessionOverlayTitleUploadFailed');
      result.subTitle = resourceHelper.getString('SessionOverlaySubTitleUploadedOrFailed');
    }

    return result;
  });

  const progressLabel = ref(resourceHelper.getString('UploadProgress'));

  const linkAriaDescription = computed(() => {
    return uploadManager.anyUploadsFailed ? [
      resourceHelper.getString('FailedToUploadItems', { itemCount: uploadManager.failedUploadsCounter }),
      resourceHelper.getString('SessionOverlayLinkHere')
    ].join(' ') : resourceHelper.getString('SessionOverlayLinkHere');
  });

  const iconAriaDescription = computed(() => {
    return isUploading.value ?
      resourceHelper.getString('UploadingItems', { itemCount: uploadManager.uploadsCount, percent: uploadManager.currentOverallProgress }) :
      resourceHelper.getString('UploadedItems', { itemCount: uploadManager.completedUploadsCounter });
  });

  const isUploading = computed(() => {
    return uploadManager.currentOverallProgress >= 0 && uploadManager.currentOverallProgress < 100 && !uploadManager.allUploadsCompleted;
  });

  const uploadProgress = computed(() => {
    if (uploadManager.allUploadsCompleted) {
      return '100%';
    }
    return uploadManager.currentOverallProgress + '%';
  });

  const continueInNewTabClick = () => {
    redirectHelper.saveRouteHash();
    const url = router.resolve({ path: '/' }).href;
    window.open(url, '_blank');
  };

  const continueHereClick = redirectHelper.tokenLoginRedirect;
</script>
